// ShortlinksPage — platform-admin surface for the shortlinker.
//
// Strictly broader than the studio (per-photographer) page in
// web/src/studio/ShortlinksPage.tsx — admin can:
//   - See ALL orgs + platform-internal links (org filter)
//   - Create with a chosen custom_code (live availability check)
//   - Pick 301 vs 302 (studio is forced 302)
//   - Hard-delete (step-up gated server-side)
//
// Note on URLs: every code resolves at BOTH /s/{code} AND /go/{code}
// — they share one handler. /s/ is the canonical going-forward path;
// /go/ is the legacy alias kept alive forever for old WordPress
// printed materials. Both URLs are surfaced in the UI so the operator
// knows the complete reach of any code they create.
//
// Backend: momentpix.admin.v1.PlatformShortlinkService
// (cmd/mp-admin/shortlinks_service.go).

import { useState, useEffect, useCallback, useMemo, useRef } from 'preact/hooks';
import { adminCall } from './api';

interface Shortlink {
  code: string;
  targetUrl: string;
  targetKind: string;
  redirectStatus: number;
  orgId: string;
  eventId: string;
  label: string;
  qrLogoEnabled: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  expiresAt?: string;
  archivedAt?: string;
  clickCount: number | string;
  lastClickedAt?: string;
}

interface DailyBucket {
  date: string;
  count: number | string;
  mobile: number | string;
  desktop: number | string;
  bot: number | string;
}

interface Stats {
  code: string;
  totalClicks: number | string;
  clicks24H: number | string;
  clicks7D: number | string;
  clicks30D: number | string;
  dailyBuckets: DailyBucket[];
  lastAggregatedAt?: string;
}

const SVC = 'momentpix.admin.v1.PlatformShortlinkService';

type View =
  | { kind: 'list' }
  | { kind: 'detail'; code: string }
  | { kind: 'create' };

export function ShortlinksPage() {
  const [view, setView] = useState<View>({ kind: 'list' });
  const [items, setItems] = useState<Shortlink[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [orgFilter, setOrgFilter] = useState<'all' | 'platform' | 'org'>('all');
  const [orgIdInput, setOrgIdInput] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setListError(null);
    try {
      const body: any = {
        search: search.trim(),
        onlyArchived: showArchived,
        includeArchived: showArchived,
        limit: 200,
      };
      if (orgFilter === 'platform') body.orgIdIsNull = true;
      else if (orgFilter === 'org' && orgIdInput.trim()) body.orgId = orgIdInput.trim();
      const data: any = await adminCall(SVC, 'ListShortlinks', body);
      setItems(data.shortlinks || []);
    } catch (e: any) {
      console.error('admin list shortlinks', e);
      setListError(e?.message || 'Failed to load shortlinks');
    } finally {
      setLoading(false);
    }
  }, [search, showArchived, orgFilter, orgIdInput]);

  useEffect(() => { reload(); }, [reload]);

  return (
    <div style={{ padding: 24, maxWidth: 1200 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h1 style={{ margin: '0 0 4px 0', fontSize: 24 }}>Shortlinks</h1>
          <p style={{ margin: 0, color: '#666', fontSize: 14 }}>
            All orgs + platform-internal redirects. Studio photographers manage their own org via /studio.
            Every code resolves at BOTH <code>/s/{'{code}'}</code> and <code>/go/{'{code}'}</code> — same handler, two paths.
          </p>
        </div>
        {view.kind === 'list' && (
          <button onClick={() => setView({ kind: 'create' })} style={btnPrimary}>+ New link</button>
        )}
        {view.kind !== 'list' && (
          <button onClick={() => { setView({ kind: 'list' }); reload(); }} style={btnSecondary}>← Back</button>
        )}
      </div>

      {view.kind === 'list' && (
        <ListView
          items={items}
          loading={loading}
          error={listError}
          search={search}
          onSearchChange={setSearch}
          orgFilter={orgFilter}
          onOrgFilterChange={setOrgFilter}
          orgIdInput={orgIdInput}
          onOrgIdInputChange={setOrgIdInput}
          showArchived={showArchived}
          onShowArchivedChange={setShowArchived}
          onSelect={(code) => setView({ kind: 'detail', code })}
          onRetry={reload}
        />
      )}

      {view.kind === 'create' && (
        <CreateForm
          onCreated={(sl) => { setView({ kind: 'detail', code: sl.code }); reload(); }}
          onCancel={() => setView({ kind: 'list' })}
        />
      )}

      {view.kind === 'detail' && (
        <DetailView
          code={view.code}
          onBack={() => { setView({ kind: 'list' }); reload(); }}
        />
      )}
    </div>
  );
}

// ── List ────────────────────────────────────────────────────────────────

function ListView({
  items, loading, error, search, onSearchChange,
  orgFilter, onOrgFilterChange, orgIdInput, onOrgIdInputChange,
  showArchived, onShowArchivedChange, onSelect, onRetry,
}: {
  items: Shortlink[];
  loading: boolean;
  error: string | null;
  search: string;
  onSearchChange: (s: string) => void;
  orgFilter: 'all' | 'platform' | 'org';
  onOrgFilterChange: (v: 'all' | 'platform' | 'org') => void;
  orgIdInput: string;
  onOrgIdInputChange: (s: string) => void;
  showArchived: boolean;
  onShowArchivedChange: (b: boolean) => void;
  onSelect: (code: string) => void;
  onRetry: () => void;
}) {
  return (
    <>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          type="search"
          placeholder="Search label or code…"
          value={search}
          onInput={(e) => onSearchChange((e.target as HTMLInputElement).value)}
          style={{ flex: 1, minWidth: 200, padding: '8px 12px', border: '1px solid #ddd', borderRadius: 4 }}
        />
        <select
          value={orgFilter}
          onChange={(e) => onOrgFilterChange((e.target as HTMLSelectElement).value as any)}
          style={{ padding: 8, border: '1px solid #ddd', borderRadius: 4 }}
        >
          <option value="all">All orgs</option>
          <option value="platform">Platform-internal only (NULL org)</option>
          <option value="org">Specific org…</option>
        </select>
        {orgFilter === 'org' && (
          <input
            type="text"
            placeholder="org UUID"
            value={orgIdInput}
            onInput={(e) => onOrgIdInputChange((e.target as HTMLInputElement).value)}
            style={{ flex: '0 1 320px', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 4, fontFamily: 'monospace', fontSize: 12 }}
          />
        )}
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, color: '#666' }}>
          <input type="checkbox" checked={showArchived} onChange={(e) => onShowArchivedChange((e.target as HTMLInputElement).checked)} />
          Show archived
        </label>
      </div>

      {error && (
        <div style={{ padding: 12, marginBottom: 16, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 4, color: '#991b1b' }}>
          {error}{' '}
          <button onClick={onRetry} style={{ marginLeft: 8 }}>Retry</button>
        </div>
      )}

      {loading && <p>Loading…</p>}
      {!loading && !error && items.length === 0 && (
        <p style={{ color: '#999', textAlign: 'center', padding: '48px 0' }}>No links match the filter.</p>
      )}

      {!loading && !error && items.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #ddd', textAlign: 'left' }}>
              <th style={{ padding: 8, fontWeight: 600 }}>Code</th>
              <th style={{ padding: 8, fontWeight: 600 }}>Label</th>
              <th style={{ padding: 8, fontWeight: 600 }}>Target</th>
              <th style={{ padding: 8, fontWeight: 600 }}>Org</th>
              <th style={{ padding: 8, fontWeight: 600 }}>Status</th>
              <th style={{ padding: 8, fontWeight: 600, textAlign: 'right' }}>Clicks</th>
            </tr>
          </thead>
          <tbody>
            {items.map((sl) => (
              <tr
                key={sl.code}
                onClick={() => onSelect(sl.code)}
                style={{
                  borderBottom: '1px solid #eee',
                  cursor: 'pointer',
                  opacity: sl.archivedAt ? 0.6 : 1,
                }}
              >
                <td style={{ padding: 8, fontFamily: 'monospace', color: '#d97706' }}>/s/{sl.code}</td>
                <td style={{ padding: 8 }}>{sl.label || <span style={{ color: '#999' }}>—</span>}</td>
                <td style={{ padding: 8, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#666' }}>{sl.targetUrl}</td>
                <td style={{ padding: 8, fontFamily: 'monospace', fontSize: 11 }}>
                  {sl.orgId ? <span title={sl.orgId}>{sl.orgId.slice(0, 8)}…</span> : <span style={{ color: '#d97706' }}>PLATFORM</span>}
                </td>
                <td style={{ padding: 8 }}>
                  {sl.archivedAt && <span style={{ background: '#ddd', padding: '2px 6px', borderRadius: 3, fontSize: 11 }}>archived</span>}
                  {!sl.archivedAt && <span style={{ background: '#fef3c7', padding: '2px 6px', borderRadius: 3, fontSize: 11 }}>{sl.redirectStatus}</span>}
                </td>
                <td style={{ padding: 8, textAlign: 'right', fontWeight: 600 }}>{String(sl.clickCount ?? 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

// ── Create form ─────────────────────────────────────────────────────────

function CreateForm({
  onCreated, onCancel,
}: {
  onCreated: (sl: Shortlink) => void;
  onCancel: () => void;
}) {
  const [label, setLabel] = useState('');
  const [targetUrl, setTargetUrl] = useState('');
  const [customCode, setCustomCode] = useState('');
  const [redirectStatus, setRedirectStatus] = useState<301 | 302>(302);
  const [orgScope, setOrgScope] = useState<'platform' | 'org'>('platform');
  const [orgId, setOrgId] = useState('');
  const [eventId, setEventId] = useState('');
  const [qrLogoEnabled, setQrLogoEnabled] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [codeCheck, setCodeCheck] = useState<{ checking: boolean; available?: boolean; reason?: string }>({ checking: false });
  const checkTimer = useRef<number | null>(null);

  useEffect(() => {
    if (!customCode.trim()) {
      setCodeCheck({ checking: false });
      return;
    }
    if (checkTimer.current) clearTimeout(checkTimer.current);
    checkTimer.current = window.setTimeout(async () => {
      setCodeCheck({ checking: true });
      try {
        const r: any = await adminCall(SVC, 'CheckCodeAvailability', { customCode: customCode.trim() });
        setCodeCheck({ checking: false, available: r.available, reason: r.reason });
      } catch {
        setCodeCheck({ checking: false });
      }
    }, 500);
    return () => { if (checkTimer.current) clearTimeout(checkTimer.current); };
  }, [customCode]);

  const submit = async () => {
    setError(null);
    if (!targetUrl.trim()) { setError('Target URL required'); return; }
    if (orgScope === 'org' && !orgId.trim()) { setError('Org UUID required when scoping to a specific org'); return; }
    if (customCode.trim() && codeCheck.available === false) {
      setError(codeCheck.reason === 'exists' ? 'Code already in use' : 'Invalid code format');
      return;
    }
    setSubmitting(true);
    try {
      const body: any = {
        targetUrl: targetUrl.trim(),
        redirectStatus,
        orgId: orgScope === 'org' ? orgId.trim() : '',
        eventId: eventId.trim() || undefined,
        label: label.trim(),
        qrLogoEnabled,
      };
      if (customCode.trim()) body.customCode = customCode.trim();
      const data: any = await adminCall(SVC, 'CreateShortlink', body);
      if (!data?.shortlink) {
        setError('Server returned no shortlink in response (check server logs)');
        return;
      }
      onCreated(data.shortlink);
    } catch (e: any) {
      setError(e?.message || 'Create failed');
    } finally {
      setSubmitting(false);
    }
  };

  // Live preview of both URLs the code will be reachable at.
  const finalCode = customCode.trim() || '<auto-generated 6 char>';
  const previewSlash = `https://www.momentpix.com/s/${finalCode}`;
  const previewGo = `https://www.momentpix.com/go/${finalCode}`;

  return (
    <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: 6, padding: 24, maxWidth: 700 }}>
      <h2 style={{ margin: '0 0 16px 0' }}>New shortlink</h2>

      <Field label="Label" hint="Internal name; e.g. 'Cushman bus card 2026' or 'WP legacy redirect'">
        <input type="text" value={label} onInput={(e) => setLabel((e.target as HTMLInputElement).value)} style={inputStyle} maxLength={200} />
      </Field>

      <Field label="Target URL *" hint="Where this code redirects to. Can be edited anytime — that's the point of having a shortlink.">
        <input type="url" value={targetUrl} onInput={(e) => setTargetUrl((e.target as HTMLInputElement).value)} style={inputStyle} required />
      </Field>

      <Field label="Custom code (optional)" hint="Empty = auto-generate 6-char base62. Custom = 1–64 chars, [a-zA-Z0-9_-] only (URL-safe). Anything goes — single-letter codes, mnemonics like 'mnt' or 'spring2026-cushman', the original WP code, whatever you want.">
        <input
          type="text"
          value={customCode}
          onInput={(e) => setCustomCode((e.target as HTMLInputElement).value)}
          style={{ ...inputStyle, fontFamily: 'monospace' }}
          maxLength={64}
        />
        {customCode.trim() && (
          <div style={{ marginTop: 4, fontSize: 12 }}>
            {codeCheck.checking && <span style={{ color: '#999' }}>Checking…</span>}
            {!codeCheck.checking && codeCheck.available === true && <span style={{ color: '#16a34a' }}>✓ Available</span>}
            {!codeCheck.checking && codeCheck.available === false && (
              <span style={{ color: '#dc2626' }}>
                ✗ {codeCheck.reason === 'exists' ? 'Code already in use' : 'Invalid format — only letters, digits, hyphen, underscore (1–64 chars)'}
              </span>
            )}
          </div>
        )}
      </Field>

      {/* URL preview — calls out that BOTH paths resolve. Removes the
          confusion about needing to "create a /go/ link" separately. */}
      <Field label="Reachable at">
        <div style={{ background: '#f9fafb', padding: 10, borderRadius: 4, fontFamily: 'monospace', fontSize: 12, lineHeight: 1.6 }}>
          <div>{previewSlash} <span style={{ color: '#999', fontStyle: 'italic' }}>(canonical)</span></div>
          <div>{previewGo} <span style={{ color: '#999', fontStyle: 'italic' }}>(legacy alias — for old WP-era prints)</span></div>
        </div>
      </Field>

      <Field label="Redirect type" hint="302 = browsers re-resolve every click (rebind-friendly). 301 = browsers cache forever (use only for permanent migrations like the WP cutover).">
        <select value={redirectStatus} onChange={(e) => setRedirectStatus(parseInt((e.target as HTMLSelectElement).value, 10) as 301 | 302)} style={inputStyle}>
          <option value={302}>Temporary (302) — recommended</option>
          <option value={301}>Permanent (301) — caches in browsers; only for true migrations</option>
        </select>
      </Field>

      <Field label="Org scope" hint="Platform-internal links have NULL org_id (corporate marketing, WP redirects). Specific-org scopes the link to that photographer org.">
        <select value={orgScope} onChange={(e) => setOrgScope((e.target as HTMLSelectElement).value as 'platform' | 'org')} style={inputStyle}>
          <option value="platform">Platform-internal (NULL org)</option>
          <option value="org">Specific org…</option>
        </select>
      </Field>

      {orgScope === 'org' && (
        <Field label="Org UUID">
          <input
            type="text"
            value={orgId}
            onInput={(e) => setOrgId((e.target as HTMLInputElement).value)}
            placeholder="paste from Orgs tab"
            style={{ ...inputStyle, fontFamily: 'monospace', fontSize: 12 }}
          />
        </Field>
      )}

      <Field label="QR logo">
        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input type="checkbox" checked={qrLogoEnabled} onChange={(e) => setQrLogoEnabled((e.target as HTMLInputElement).checked)} />
          <span style={{ fontSize: 14 }}>Include MomentPix logo overlay</span>
        </label>
      </Field>

      {error && (
        <div style={{ color: '#dc2626', fontSize: 14, margin: '12px 0', padding: 8, background: '#fef2f2', borderRadius: 4 }}>{error}</div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 24, justifyContent: 'flex-end' }}>
        <button onClick={onCancel} disabled={submitting} style={btnSecondary}>Cancel</button>
        <button onClick={submit} disabled={submitting} style={btnPrimary}>
          {submitting ? 'Creating…' : 'Create'}
        </button>
      </div>
    </div>
  );
}

// ── Detail ──────────────────────────────────────────────────────────────

function DetailView({
  code, onBack,
}: {
  code: string;
  onBack: () => void;
}) {
  const [sl, setSl] = useState<Shortlink | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [qrSize, setQrSize] = useState(1024);
  const [qrLogoOverride, setQrLogoOverride] = useState<boolean | null>(null);

  const reload = useCallback(async () => {
    setError(null);
    try {
      const linkData: any = await adminCall(SVC, 'GetShortlink', { code });
      if (!linkData?.shortlink) {
        setError(`Server returned no shortlink for code "${code}". Response: ${JSON.stringify(linkData).slice(0, 200)}`);
        return;
      }
      setSl(linkData.shortlink);

      // Stats fetched separately — failure here doesn't break the page,
      // just leaves the stats panel empty. The link itself is more
      // important than analytics for "I need to edit this code right now."
      try {
        const statsData: any = await adminCall(SVC, 'GetShortlinkStats', { code });
        setStats(statsData);
      } catch (statsErr) {
        console.warn('shortlinks: stats fetch failed', statsErr);
        setStats(null);
      }
    } catch (e: any) {
      console.error('shortlinks: load detail failed', e);
      setError(e?.message || 'Failed to load shortlink');
    }
  }, [code]);

  useEffect(() => { reload(); }, [reload]);

  // Visible loading + error states. Earlier version returned <p>Loading…</p>
  // which can be missed if the page styles hide it; this version uses a
  // bordered container so it's always visible against the admin shell.

  if (error) {
    return (
      <div style={{ padding: 16, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6 }}>
        <strong style={{ color: '#991b1b' }}>Couldn't load this shortlink.</strong>
        <div style={{ marginTop: 8, color: '#991b1b', fontSize: 14 }}>{error}</div>
        <div style={{ marginTop: 12 }}>
          <button onClick={reload} style={{ ...btnSecondary, marginRight: 8 }}>Retry</button>
          <button onClick={onBack} style={btnSecondary}>← Back to list</button>
        </div>
      </div>
    );
  }

  if (!sl) {
    return (
      <div style={{ padding: 16, background: '#f9fafb', border: '1px solid #ddd', borderRadius: 6 }}>
        Loading <code style={{ fontFamily: 'monospace' }}>/s/{code}</code>…
      </div>
    );
  }

  const fullSlash = `https://www.momentpix.com/s/${sl.code}`;
  const fullGo = `https://www.momentpix.com/go/${sl.code}`;

  const downloadQR = async () => {
    try {
      const data: any = await adminCall(SVC, 'GetShortlinkQR', {
        code: sl.code,
        size: qrSize,
        overrideLogo: qrLogoOverride !== null,
        logoEnabled: qrLogoOverride === null ? sl.qrLogoEnabled : qrLogoOverride,
      });
      const blob = b64toBlob(data.png, 'image/png');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = data.filename || `mp-${sl.code}-qr-${qrSize}.png`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      alert(e?.message || 'QR download failed');
    }
  };

  const archive = async () => {
    if (!confirm(`Archive ${sl.code}? It will return 410 Gone on click. Reversible.`)) return;
    try { await adminCall(SVC, 'ArchiveShortlink', { code: sl.code }); reload(); }
    catch (e: any) { alert(e?.message || 'Archive failed'); }
  };

  const unarchive = async () => {
    try { await adminCall(SVC, 'UnarchiveShortlink', { code: sl.code }); reload(); }
    catch (e: any) { alert(e?.message || 'Unarchive failed'); }
  };

  const hardDelete = async () => {
    const typed = prompt(`HARD DELETE is irreversible. The row + aggregates are removed forever.\n\nType the code "${sl.code}" to confirm:`);
    if (typed !== sl.code) {
      if (typed !== null) alert('Code did not match. Delete cancelled.');
      return;
    }
    try {
      await adminCall(SVC, 'HardDeleteShortlink', { code: sl.code });
      onBack();
    } catch (e: any) {
      alert(e?.message || 'Hard delete failed (step-up may be required — re-authenticate and try again)');
    }
  };

  const copyText = (text: string, what: string) => {
    navigator.clipboard.writeText(text).then(
      () => alert(`Copied: ${what}`),
      () => alert('Copy failed — select manually'),
    );
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 24, alignItems: 'start' }}>
      <div>
        <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: 6, padding: 20, marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, gap: 12 }}>
            <div>
              <div style={{ fontFamily: 'monospace', fontSize: 14, color: '#666', marginBottom: 2 }}>
                /s/{sl.code}
              </div>
              <h2 style={{ margin: 0, fontSize: 20 }}>{sl.label || '(no label)'}</h2>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {!editing && !sl.archivedAt && <button onClick={() => setEditing(true)} style={btnSecondary}>Edit</button>}
              {!editing && !sl.archivedAt && <button onClick={archive} style={btnSecondary}>Archive</button>}
              {!editing && sl.archivedAt && <button onClick={unarchive} style={btnPrimary}>Restore</button>}
              {!editing && (
                <button onClick={hardDelete} style={btnDanger}>Hard delete</button>
              )}
            </div>
          </div>

          {!editing && (
            <>
              <Field label="Target URL">
                <div style={{ fontFamily: 'monospace', fontSize: 13, padding: 8, background: '#f9fafb', borderRadius: 4, wordBreak: 'break-all' }}>{sl.targetUrl}</div>
              </Field>

              {/* Both reachable URLs — the resolution to "where does /go/{code} come from?" */}
              <Field label="Reachable at">
                <div style={{ background: '#f9fafb', padding: 10, borderRadius: 4, fontFamily: 'monospace', fontSize: 12, lineHeight: 1.7 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <span>{fullSlash} <span style={{ color: '#999', fontStyle: 'italic' }}>(canonical)</span></span>
                    <button onClick={() => copyText(fullSlash, '/s/ URL')} style={{ ...btnTiny, fontSize: 11 }}>Copy</button>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <span>{fullGo} <span style={{ color: '#999', fontStyle: 'italic' }}>(legacy alias)</span></span>
                    <button onClick={() => copyText(fullGo, '/go/ URL')} style={{ ...btnTiny, fontSize: 11 }}>Copy</button>
                  </div>
                </div>
              </Field>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                <Field label="Redirect">{sl.redirectStatus} {sl.redirectStatus === 301 ? '(permanent)' : '(temporary)'}</Field>
                <Field label="Org">{sl.orgId ? <span style={{ fontFamily: 'monospace', fontSize: 12 }} title={sl.orgId}>{sl.orgId.slice(0, 8)}…</span> : <span style={{ color: '#d97706' }}>platform-internal</span>}</Field>
                <Field label="QR logo">{sl.qrLogoEnabled ? 'on' : 'off'}</Field>
              </div>
            </>
          )}

          {editing && (
            <EditForm sl={sl} onSaved={() => { setEditing(false); reload(); }} onCancel={() => setEditing(false)} />
          )}
        </div>

        {stats && (
          <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: 6, padding: 20 }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: 16 }}>Click activity</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
              <Stat label="Total" value={Number(stats.totalClicks)} />
              <Stat label="24h" value={Number(stats.clicks24H)} />
              <Stat label="7d" value={Number(stats.clicks7D)} />
              <Stat label="30d" value={Number(stats.clicks30D)} />
            </div>
            <Sparkline buckets={stats.dailyBuckets || []} />
            {stats.lastAggregatedAt && (
              <div style={{ marginTop: 12, fontSize: 11, color: '#999' }}>
                Updated {formatRelative(stats.lastAggregatedAt)}. Aggregator runs every 5 min.
              </div>
            )}
          </div>
        )}
      </div>

      <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: 6, padding: 20 }}>
        <h3 style={{ margin: '0 0 12px 0', fontSize: 16 }}>QR code</h3>
        <Field label="Size">
          <select value={qrSize} onChange={(e) => setQrSize(parseInt((e.target as HTMLSelectElement).value, 10))} style={inputStyle}>
            <option value={1024}>1024 (print)</option>
            <option value={512}>512 (medium)</option>
            <option value={256}>256 (web)</option>
          </select>
        </Field>
        <Field label="Logo overlay">
          <select
            value={qrLogoOverride === null ? 'default' : qrLogoOverride ? 'on' : 'off'}
            onChange={(e) => {
              const v = (e.target as HTMLSelectElement).value;
              setQrLogoOverride(v === 'default' ? null : v === 'on');
            }}
            style={inputStyle}
          >
            <option value="default">Default ({sl.qrLogoEnabled ? 'on' : 'off'})</option>
            <option value="on">Logo on</option>
            <option value="off">Plain</option>
          </select>
        </Field>
        <button onClick={downloadQR} style={{ ...btnPrimary, width: '100%', marginTop: 8 }}>Download QR</button>
        <div style={{ marginTop: 12, fontSize: 11, color: '#999', lineHeight: 1.5 }}>
          QR encodes the canonical <code style={{ fontFamily: 'monospace' }}>/s/{sl.code}</code> URL. Both paths resolve, but /s/ is the recommended print form.
        </div>
      </div>
    </div>
  );
}

// ── Edit form ───────────────────────────────────────────────────────────

function EditForm({
  sl, onSaved, onCancel,
}: {
  sl: Shortlink;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [label, setLabel] = useState(sl.label);
  const [targetUrl, setTargetUrl] = useState(sl.targetUrl);
  const [eventId, setEventId] = useState(sl.eventId);
  const [redirectStatus, setRedirectStatus] = useState(sl.redirectStatus);
  const [qrLogoEnabled, setQrLogoEnabled] = useState(sl.qrLogoEnabled);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const body: any = { code: sl.code };
      if (label !== sl.label) body.label = label;
      if (targetUrl !== sl.targetUrl) body.targetUrl = targetUrl;
      if (eventId !== sl.eventId) body.eventId = eventId;
      if (redirectStatus !== sl.redirectStatus) body.redirectStatus = redirectStatus;
      if (qrLogoEnabled !== sl.qrLogoEnabled) body.qrLogoEnabled = qrLogoEnabled;
      await adminCall(SVC, 'UpdateShortlink', body);
      onSaved();
    } catch (e: any) {
      setError(e?.message || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Field label="Label">
        <input type="text" value={label} onInput={(e) => setLabel((e.target as HTMLInputElement).value)} style={inputStyle} maxLength={200} />
      </Field>
      <Field label="Target URL">
        <input type="url" value={targetUrl} onInput={(e) => setTargetUrl((e.target as HTMLInputElement).value)} style={inputStyle} />
      </Field>
      <Field label="Event UUID (optional)">
        <input type="text" value={eventId} onInput={(e) => setEventId((e.target as HTMLInputElement).value)} style={{ ...inputStyle, fontFamily: 'monospace', fontSize: 12 }} />
      </Field>
      <Field label="Redirect">
        <select value={redirectStatus} onChange={(e) => setRedirectStatus(parseInt((e.target as HTMLSelectElement).value, 10))} style={inputStyle}>
          <option value={302}>Temporary (302)</option>
          <option value={301}>Permanent (301)</option>
        </select>
      </Field>
      <Field label="QR logo">
        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input type="checkbox" checked={qrLogoEnabled} onChange={(e) => setQrLogoEnabled((e.target as HTMLInputElement).checked)} />
          <span>MomentPix logo overlay</span>
        </label>
      </Field>

      {error && <div style={{ color: '#dc2626', fontSize: 14, margin: '12px 0' }}>{error}</div>}

      <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
        <button onClick={onCancel} disabled={submitting} style={btnSecondary}>Cancel</button>
        <button onClick={submit} disabled={submitting} style={btnPrimary}>{submitting ? 'Saving…' : 'Save'}</button>
      </div>
    </>
  );
}

// ── Subcomponents ───────────────────────────────────────────────────────

function Field({ label, hint, children }: { label: string; hint?: string; children: any }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 12, color: '#666', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>{label}</label>
      {children}
      {hint && <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div style={{ textAlign: 'center', padding: 8 }}>
      <div style={{ fontSize: 22, fontWeight: 700 }}>{value.toLocaleString()}</div>
      <div style={{ fontSize: 11, color: '#999', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
    </div>
  );
}

function Sparkline({ buckets }: { buckets: DailyBucket[] }) {
  const max = useMemo(() => Math.max(1, ...buckets.map((b) => Number(b.count) || 0)), [buckets]);
  if (buckets.length === 0) return <p style={{ color: '#999', fontSize: 13, margin: 0 }}>No clicks yet.</p>;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 60, padding: '4px 0', borderBottom: '1px solid #eee' }}>
      {buckets.map((b) => {
        const cnt = Number(b.count);
        const h = Math.max(2, Math.round((cnt / max) * 56));
        return (
          <div
            key={b.date}
            title={`${b.date}: ${cnt} (m${b.mobile}/d${b.desktop}/b${b.bot})`}
            style={{ flex: 1, minWidth: 4, height: h, background: '#d97706', borderRadius: '1px 1px 0 0', opacity: cnt === 0 ? 0.2 : 1 }}
          />
        );
      })}
    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid #ddd',
  borderRadius: 4,
  fontSize: 14,
  fontFamily: 'inherit',
  boxSizing: 'border-box' as const,
};

const btnPrimary = {
  background: '#d97706',
  color: 'white',
  border: 'none',
  padding: '8px 16px',
  borderRadius: 4,
  cursor: 'pointer',
  fontSize: 14,
  fontWeight: 600,
};

const btnSecondary = {
  background: 'white',
  color: '#374151',
  border: '1px solid #ddd',
  padding: '8px 16px',
  borderRadius: 4,
  cursor: 'pointer',
  fontSize: 14,
};

const btnDanger = {
  background: '#dc2626',
  color: 'white',
  border: 'none',
  padding: '6px 12px',
  borderRadius: 4,
  cursor: 'pointer',
  fontSize: 14,
};

const btnTiny = {
  background: 'white',
  color: '#374151',
  border: '1px solid #ddd',
  padding: '2px 8px',
  borderRadius: 3,
  cursor: 'pointer',
  fontSize: 11,
};

function formatRelative(iso: string): string {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function b64toBlob(b64Data: string, contentType: string): Blob {
  const byteChars = atob(b64Data);
  const byteNumbers = new Array<number>(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
  return new Blob([new Uint8Array(byteNumbers)], { type: contentType });
}
